/* eslint-disable */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const ParentAppLockContext = createContext(null);
export const BIOMETRIC_UNLOCK_SESSION_KEY = "pp_parent_biometric_unlocked_at";

/*
  PresentPlus biometric model for this build:

  - Face ID / Touch ID is used for secure login from the Parent Login screen.
  - The parent app shell must NOT lock/blank itself when the app is minimised,
    resumed, or when the parent changes route inside the app.

  Why:
  The previous app-lock flow listened to appStateChange / visibilitychange and
  set isUnlocked=false while the user was already inside the app. ParentLayout
  then swapped the current screen for ParentBiometricGate. On iOS WebView this
  could happen during route changes or while native biometric/login state was
  settling, causing a flicker followed by a blank screen.

  This provider keeps the same public API so existing imports do not break, but
  it is intentionally non-blocking. Biometric login remains handled by:
  - src/pages/ParentLogin.jsx
  - src/pages/ParentProfile.jsx
  - src/utils/biometricAuth.js
  - src/services/biometricService.js
*/

export function ParentAppLockProvider({ children }) {
  const [unlockError, setUnlockError] = useState("");

  const refreshLockState = useCallback(async () => {
    setUnlockError("");
    return { success: true, skipped: true };
  }, []);

  const unlockNow = useCallback(async () => {
    try {
      sessionStorage.setItem(BIOMETRIC_UNLOCK_SESSION_KEY, String(Date.now()));
    } catch {}

    setUnlockError("");
    return { success: true, skipped: true };
  }, []);

  const setUnlocked = useCallback(() => {
    setUnlockError("");
  }, []);

  const value = useMemo(
    () => ({
      biometricEnabled: false,
      isUnlocked: true,
      checkingLock: false,
      unlockBusy: false,
      unlockError,
      refreshLockState,
      unlockNow,
      setUnlocked,
    }),
    [unlockError, refreshLockState, unlockNow, setUnlocked]
  );

  return (
    <ParentAppLockContext.Provider value={value}>
      {children}
    </ParentAppLockContext.Provider>
  );
}

export function useParentAppLock() {
  const ctx = useContext(ParentAppLockContext);
  if (!ctx) {
    throw new Error("useParentAppLock must be used within ParentAppLockProvider");
  }
  return ctx;
}
